import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AnalyticsService,
  DropdownItem,
  DynamicTableColDef,
  FrkBreakpoint,
} from '@frk/eds-components';
import {
  PortfolioHolding,
  PortfolioTopHoldingTable,
  PortfolioTopTenHoldingMoneyFunds,
} from '@models';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { DebugService, SiteConfigService } from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import { AssetClass, ConfigurationId, FundId, PortfolioDisplay } from '@types';
import { Logger } from '@utils/logger';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PortfolioDownloadService } from '../services/portfolio-download.service';
import { PortfolioHoldingsService } from '../services/portfolio-holdings.service';

const logger = Logger.getLogger('PortfolioHoldingsComponent');

export interface PortfolioHoldingData {
  fundId: FundId;
  assetClass: AssetClass;
  fundName: string;
  title: string;
  caveatPlacement?: string;
  secondaryCaveatPlacement?: string;
  proximalTop?: string;
  secondaryProximalTop?: string;
  proximalBottom?: string;
  secondaryProximalBottom?: string;
  tooltip?: string;
  asOfLabel?: string;
  asOfDate?: string;
  calculationBasis?: string;
  calculationType?: string;
  updateFrequency?: string;
  productType: ConfigurationId;
  holdings:
    | PortfolioHolding[]
    | PortfolioTopTenHoldingMoneyFunds[]
    | PortfolioTopHoldingTable[];
  hideColumns?: string[];
  isTopHoldingData?: boolean;
  benchmarkName?: string;
  isTopTenHoldingsMoneyFunds?: boolean;
  filteredAsOfDate?: DropdownItem[];
}

@Component({
  selector: 'ft-portfolio-holdings',
  templateUrl: './portfolio-holdings.component.html',
  styleUrls: ['./portfolio-holdings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortfolioHoldingsComponent
  extends BaseProductDetailComponent
  implements PortfolioDisplay, OnInit, OnDestroy {
  @Input() data: PortfolioHoldingData;
  public isPopulated = false;
  public asOfDate?: string;
  private selectedAsOfDate?: DropdownItem;
  public breakpoint: FrkBreakpoint = FrkBreakpoint.LargeTablet;
  private allCols: DynamicTableColDef<PortfolioHolding>[] = [];
  public colDefs: DynamicTableColDef<PortfolioHolding>[] = [];
  public rowData: PortfolioHolding[] = [];

  // xls props
  private xlsColDefs: DynamicTableColDef<PortfolioHolding>[] = [];
  private xlsRowData: PortfolioHolding[] = [];

  /**
   * Custom parameters to be used in edsButton directive
   */
  private eventParams = {};
  private unsubscribe$: Subject<void> = new Subject<void>();
  private expandAll = false;

  constructor(
    private translateService: TranslateService,
    private siteConfigService: SiteConfigService,
    private analytics: AnalyticsService,
    private downloadService: PortfolioDownloadService,
    private holdingsService: PortfolioHoldingsService,
    private debugService: DebugService // private agGridService: AgGridService
  ) {
    super();
  }

  ngOnInit() {
    this.createEventParams(this.data);

    this.debugService
      .getExpandAllComponents$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((expandAll: boolean) => {
        if (expandAll) {
          this.expandAll = expandAll;
          this.processData();
        }
      });
  }

  /**
   * When data is passed into this component, this function checks if it can be displayed
   * Called direct from Entry component, as ngOnInit and ngOnchanges not called when dynamic component
   */
  public processData(): void {
    logger.debug('processData()', this.data);

    if (this.data?.holdings?.length > 0) {
      // set initial asOfDate
      if (!this.asOfDate) {
        this.asOfDate = this.data.asOfDate;
      }
      // set initial selectedAsOfDate
      if (!this.selectedAsOfDate && this.data.filteredAsOfDate?.length > 0) {
        this.selectedAsOfDate = this.data.filteredAsOfDate[0];
        this.asOfDate = this.selectedAsOfDate.label;
      }

      // Fetch colDefs as per product type received.
      const showMoneyfund: boolean =
        this.data.productType === ConfigurationId.MMF &&
        this.siteConfigService.isSiteInternational() &&
        this.data.isTopTenHoldingsMoneyFunds;
      if (showMoneyfund) {
        // Money Fund
        this.colDefs = this.getMoneyFundsCols();
        this.xlsColDefs = this.colDefs;
        // get DT data, sorted by weight desc
        this.rowData = this.data.holdings?.sort(
          (
            a: PortfolioTopTenHoldingMoneyFunds,
            b: PortfolioTopTenHoldingMoneyFunds
          ): number => Number(b.holdingValueStd) - Number(a.holdingValueStd)
        );
      } else if (this.data.isTopHoldingData) {
        // Top Holdings
        this.colDefs = this.getTopHoldingsCols();
        this.xlsColDefs = this.colDefs;
        // get DT data, sorted by weight desc
        this.rowData = this.data.holdings?.sort(
          (a: PortfolioTopHoldingTable, b: PortfolioTopHoldingTable): number =>
            Number(b.breakdownPercentage) - Number(a.breakdownPercentage)
        );
      } else {
        // Full / Daily Holdings
        this.allCols = this.holdingsService.getAllCols(this.data.fundId);
        this.colDefs = this.getFullHoldingCols(
          this.data.fundId,
          this.data.assetClass
        );
        // xls displays all columns instead of specific set
        this.xlsColDefs = this.allCols;
        // get DT data, sorted by weight desc
        this.rowData = this.data.holdings?.sort(
          (a: PortfolioHolding, b: PortfolioHolding): number =>
            Number(b.percentageOfNetAssets) - Number(a.percentageOfNetAssets)
        );
      }

      // filter by asOfDate
      if (this.selectedAsOfDate && this.data.filteredAsOfDate?.length > 1) {
        this.rowData = this.rowData.filter(
          (row: PortfolioHolding): boolean =>
            row.asOfDateStd === this.selectedAsOfDate.value
        );
      }

      // xls will use all rows
      this.xlsRowData = this.rowData.slice(); // slice() creates shallow copy

      // set xls colDefs. This could be different from colDefs depending on empty cells
      this.xlsColDefs = this.holdingsService.removeEmptyCols(
        this.xlsColDefs,
        this.xlsRowData
      );

      // remove unused (i.e. no data) cols from colDefs
      this.colDefs = this.holdingsService.removeEmptyCols(
        this.colDefs,
        this.rowData
      );

      logger.debug('colDefs, rowData', this.colDefs, this.rowData);

      // Finally, set visibility of holding to be true, when everything is done.
      this.isPopulated = true;
    }
  }

  public filterOnDate(selectedItem: DropdownItem): void {
    this.selectedAsOfDate = selectedItem;
    this.asOfDate = this.selectedAsOfDate.label;
    this.processData();
  }

  /**
   * Parses params and converts them to event object
   */
  private createEventParams(params: PortfolioHoldingData): void {
    this.eventParams = {
      location: '',
      file_name: params?.fundName,
      file_extension: 'xlsx',
      event_data: {
        file_extension: 'xlsx',
        file_lit_code: '',
        file_name: params?.fundName,
        link_url: 'TBD',
        location: '',
      },
    };
  }

  /**
   * Handle download action.
   * Convert table records into excel format and initiate download.
   */
  public triggerDownloadAction(): void {
    this.downloadService.downloadHoldings(
      this.data.fundName,
      this.asOfDate,
      this.xlsColDefs,
      this.xlsRowData
    );

    this.analytics.trackEvent({
      event: 'file_download',
      detailed_event: 'Download Link Clicked',
      ...this.eventParams,
    });
  }

  private getFullHoldingCols = (
    fundId: FundId,
    assetClass: AssetClass
  ): DynamicTableColDef<PortfolioHolding>[] => {
    const colSet: string[] = this.siteConfigService.getHoldingsTableColumns(
      fundId,
      assetClass
    );
    logger.debug('matched colset', colSet, colSet.map(this.getCol));
    return colSet.map(this.getCol);
  };

  private getCol = (field: string): DynamicTableColDef<PortfolioHolding> =>
    this.allCols.find(
      (col: DynamicTableColDef<PortfolioHolding>): boolean =>
        col.field === field
    );

  private getMoneyFundsCols = (): DynamicTableColDef<PortfolioHolding>[] => [
    {
      field: 'securityName',
      headerName: this.translateService.instant('pds.SECURITY_NAME'),
      footnotePlacement: `Holdings:SecurityName`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-security-name'
      ),
      sortable: true,
    },
    {
      field: 'holdingValue',
      headerName: this.translateService.instant('pds.PCT_OF_NET_ASSETS'),
      footnotePlacement: `Holdings:HoldingValue`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-holding-value'
      ),
      dataType: 'percent',
      sortable: true,
    },
    {
      field: 'maturityDate',
      headerName: this.translateService.instant('pds.SECURITY_EXPIRATION_DATE'),
      footnotePlacement: `Holdings:MaturityDate`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-maturity-date'
      ),
      dataType: 'date',
      sortable: true,
    },
    {
      field: 'assetType',
      headerName: this.translateService.instant('pds.ASSET_CLASS_CATEGORY'),
      footnotePlacement: `Holdings:AssetType`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-asset-type'
      ),
      sortable: true,
    },
    {
      field: 'countryOfIssue',
      headerName: this.translateService.instant('pds.GEOGRAPHY_CODE'),
      footnotePlacement: `Holdings:CountryOfIssue`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-country-of-issue'
      ),
      sortable: true,
    },
  ];

  private getTopHoldingsCols = (): DynamicTableColDef<PortfolioHolding>[] => [
    {
      field: 'holdingName',
      headerName: this.translateService.instant(
        'pds.SECURITY_NAME',
        this.brConfig?.fundId
      ),
      footnotePlacement: `Holdings:HoldingName`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-holding-name'
      ),
      sortable: true,
    },
    {
      field: 'sectorName',
      headerName: this.translateService.instant(
        'pds.SECTOR_NAME',
        this.brConfig?.fundId
      ),
      footnotePlacement: `Holdings:SectorName`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-sector-name'
      ),
      sortable: true,
    },
    {
      field: 'breakdownPercentage',
      headerName: this.translateService.instant(
        'pds.PCT_OF_NET_ASSETS',
        this.brConfig?.fundId
      ),
      footnotePlacement: `Holdings:BreakdownPercentage`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-breakdown-percentage'
      ),
      dataType: 'percent',
      sortable: true,
    },
    {
      field: 'geoCode',
      headerName: this.translateService.instant(
        'pds.GEOGRAPHY_CODE',
        this.brConfig?.fundId
      ),
      footnotePlacement: `Holdings:GeoCode`,
      tooltipContent: this.translateService.tooltip(
        'products.portfolio-holdings-geo-code'
      ),
      sortable: true,
    },
  ];

  /**
   * Remove the subscribed observables on page destroy.
   */
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
